// useMediaDevices — thin wrapper around the Stream Video SDK device APIs
// that the new CallModal dropdowns consume.
//
// Why a wrapper instead of calling the SDK directly:
//   1. Single, stable API surface for the dropdowns (CallModal stays clean).
//   2. Speaker selection isn't supported in all browsers (Firefox, mobile
//      Safari). The SDK exposes that via `isDeviceSelectionSupported`; we
//      hide the speaker dropdown entirely when it's false.
//   3. Audio-level metering for the mic uses Web-Audio on the active
//      microphone MediaStream — that's standalone, not in the SDK.
//
// The SDK already calls `navigator.mediaDevices.enumerateDevices()` for us
// and keeps `devices` reactive. We don't re-enumerate manually.

import { useEffect, useRef, useState } from "react";
import { useCallStateHooks } from "@stream-io/video-react-sdk";

export default function useMediaDevices() {
  const {
    useMicrophoneState,
    useCameraState,
    useSpeakerState,
  } = useCallStateHooks();

  const micState = useMicrophoneState();
  const camState = useCameraState();
  const spkState = useSpeakerState();

  // Normalise — different SDK builds expose these slightly differently.
  const mic = {
    devices: micState.devices || [],
    selectedDeviceId: micState.selectedDevice || null,
    mediaStream: micState.mediaStream || null,
    hasBrowserPermission: micState.hasBrowserPermission !== false,
    select: async (deviceId) => {
      if (!micState.microphone) return;
      try {
        await micState.microphone.select(deviceId);
      } catch (err) {
        console.error("Mic select failed:", err);
        throw err;
      }
    },
  };

  const camera = {
    devices: camState.devices || [],
    selectedDeviceId: camState.selectedDevice || null,
    hasBrowserPermission: camState.hasBrowserPermission !== false,
    select: async (deviceId) => {
      if (!camState.camera) return;
      try {
        await camState.camera.select(deviceId);
      } catch (err) {
        console.error("Camera select failed:", err);
        throw err;
      }
    },
  };

  const speaker = {
    // Some browsers don't allow output device selection at all (Firefox,
    // mobile Safari). The SDK reports that via `isDeviceSelectionSupported`.
    isSupported: spkState.isDeviceSelectionSupported !== false,
    devices: spkState.devices || [],
    selectedDeviceId: spkState.selectedDevice || null,
    select: (deviceId) => {
      if (!spkState.speaker) return;
      try {
        spkState.speaker.select(deviceId);
      } catch (err) {
        console.error("Speaker select failed:", err);
      }
    },
  };

  return { mic, camera, speaker };
}

// -----------------------------------------------------------------------------
// useMicLevel — RMS-based input level on the currently active microphone
// stream. Returns a number in [0, 1]. Designed to be cheap enough to call
// during an active call (single AnalyserNode, 50ms RAF tick).
//
// We deliberately read from the SDK's microphone.mediaStream rather than
// opening our own getUserMedia — opening a second stream would compete
// with the one Stream is already publishing.
// -----------------------------------------------------------------------------
export function useMicLevel(enabled = true) {
  const { useMicrophoneState } = useCallStateHooks();
  const { mediaStream } = useMicrophoneState();
  const [level, setLevel] = useState(0);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const srcRef = useRef(null);

  useEffect(() => {
    if (!enabled || !mediaStream) {
      setLevel(0);
      return undefined;
    }

    let cancelled = false;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return undefined;

      const ctx = new AudioCtx();
      const src = ctx.createMediaStreamSource(mediaStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      src.connect(analyser);

      ctxRef.current = ctx;
      analyserRef.current = analyser;
      srcRef.current = src;

      const data = new Uint8Array(analyser.frequencyBinCount);
      let lastTick = 0;

      const tick = (ts) => {
        if (cancelled) return;
        // Throttle to ~20Hz — visual smoothness is plenty at that rate
        // and saves a chunk of CPU on lower-end laptops during calls.
        if (ts - lastTick >= 50) {
          analyser.getByteTimeDomainData(data);
          // RMS over the 128-byte block, normalised against 128 (silence).
          let sumSquares = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / data.length);
          // Map [0, 0.5] -> [0, 1] so normal speech reaches ~0.7 instead
          // of crawling around 0.1.
          setLevel(Math.min(1, rms * 2));
          lastTick = ts;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      console.error("useMicLevel setup failed:", err);
    }

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try {
        if (srcRef.current) srcRef.current.disconnect();
        if (analyserRef.current) analyserRef.current.disconnect();
        if (ctxRef.current && ctxRef.current.state !== "closed") {
          ctxRef.current.close();
        }
      } catch (err) {
        // AudioContext close can throw if it's already closing in some
        // browsers; not a real problem, just noisy in logs.
      }
    };
  }, [enabled, mediaStream]);

  return level;
}
